import { CommonModule } from '@angular/common';
import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import {
	MAT_DIALOG_DATA,
	MatDialogRef,
	MatDialogModule,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CardError, CardErrorSeverity } from '../../../models/card-model';

/**
 * Data passed to the error details modal
 */
export interface ErrorDetailsModalData {
	error: CardError;
	cardId?: string;
	cardTitle?: string;
}

/**
 * Modal component for displaying detailed error information.
 * Used when card error details need to be viewed without taking up card space.
 */
@Component({
	selector: 'jepto-error-details-modal',
	standalone: true,
	templateUrl: './jepto-error-details-modal.component.html',
	styleUrls: ['./jepto-error-details-modal.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule],
})
export class JeptoErrorDetailsModalComponent {
	constructor(
		public dialogRef: MatDialogRef<JeptoErrorDetailsModalComponent>,
		@Inject(MAT_DIALOG_DATA) public data: ErrorDetailsModalData
	) {}

	/**
	 * Returns the CSS class for the error severity
	 */
	getSeverityClass(): string {
		switch (this.data.error.severity) {
			case CardErrorSeverity.Info:
				return 'severity-info';
			case CardErrorSeverity.Warning:
				return 'severity-warning';
			case CardErrorSeverity.Critical:
				return 'severity-critical';
			case CardErrorSeverity.Error:
			default:
				return 'severity-error';
		}
	}

	/**
	 * Returns the Material icon name for the error severity
	 */
	getSeverityIcon(): string {
		switch (this.data.error.severity) {
			case CardErrorSeverity.Info:
				return 'info';
			case CardErrorSeverity.Warning:
				return 'warning';
			case CardErrorSeverity.Critical:
				return 'error';
			case CardErrorSeverity.Error:
			default:
				return 'error_outline';
		}
	}

	/**
	 * Copies the error details to clipboard for sharing with support
	 */
	copyToClipboard(): void {
		const errorText = this.formatErrorForClipboard();
		navigator.clipboard.writeText(errorText).then(() => {
			// Could show a snackbar here if desired
			console.log(
				'[ErrorDetailsModal] Error details copied to clipboard'
			);
		});
	}

	/**
	 * Formats the error details for clipboard copying
	 */
	private formatErrorForClipboard(): string {
		const error = this.data.error;
		const lines = [
			`Error Report`,
			`============`,
			``,
			`Card: ${this.data.cardTitle || this.data.cardId || 'Unknown'}`,
			`Timestamp: ${error.timestamp}`,
			``,
			`Error Code: ${error.code}`,
			`Severity: ${error.severity}`,
			`Message: ${error.message}`,
		];

		if (error.requestId) {
			lines.push(`Request ID: ${error.requestId}`);
		}
		if (error.executionId) {
			lines.push(`Execution ID: ${error.executionId}`);
		}
		if (error.details) {
			lines.push(``, `Technical Details:`, error.details);
		}
		if (error.suggestedAction) {
			lines.push(``, `Suggested Action: ${error.suggestedAction}`);
		}
		if (error.context) {
			lines.push(``, `Context:`, JSON.stringify(error.context, null, 2));
		}
		if (error.stack) {
			lines.push(``, `Stack Trace:`, error.stack);
		}

		return lines.join('\n');
	}

	close(): void {
		this.dialogRef.close();
	}
}
